import React, { FC, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getProduct } from 'features/ProductDetails/productForm';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined';
import Checkbox from '@material-ui/core/Checkbox';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import MoreVert from '@material-ui/icons/MoreVert';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { QRDialog } from 'components/QrDialog/QRDialog';
import { DocumentsDialog } from 'components/UI/Dialog/DocumentsDialog';
import { DocLinkType } from 'routes/routes';
import { SerialNumberExternal } from './SerialNumberExternal';
import { getSelectedSerialNumbers, selectSerialNumber, SerialNumber } from './serialNumbers';

interface SerialNumberRowProps {
  askConfirmDeleteSerialNumber: (serialNumber: string) => void;
  disabled: boolean;
  serialNumber: SerialNumber;
  showConfiguration: (product: { id: string; name: string }, serial: string) => void;
}

const SerialNumberRow: FC<SerialNumberRowProps> = ({
  disabled,
  serialNumber,
  showConfiguration,
  askConfirmDeleteSerialNumber
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showDocsModal, setShowDocsModal] = useState<boolean>(false);
  const product = useSelector(getProduct);
  const selectedSerialNumbers = useSelector(getSelectedSerialNumbers);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [showQrModal, setShowQrModal] = useState<boolean>(false);

  return (
    <TableRow>
      <TableCell style={{ width: '5%' }}>
        <Checkbox
          checked={selectedSerialNumbers[serialNumber.value] || false}
          onChange={(e, value) => {
            dispatch(selectSerialNumber({ key: serialNumber.value, value }));
          }}
        />
      </TableCell>
      <TableCell align="left" style={{ width: '25%' }}>
        <div>{serialNumber.value}</div>
        <div>{t('products.product quantity', { qty: serialNumber.quantity })}</div>
      </TableCell>
      <TableCell align="center" style={{ width: '20%' }}>
        <span>{moment(serialNumber.created_at).format('YYYY-MM-DD hh:mm:ss')}</span>
      </TableCell>
      <TableCell style={{ width: '40%' }}>
        <SerialNumberExternal
          disabled={disabled}
          externalValue={serialNumber.external_value}
          serialValue={serialNumber.value}
        />
      </TableCell>
      <TableCell align="right" style={{ width: '15%' }}>
        <IconButton
          disabled={!serialNumber.can_be_deleted}
          aria-label={t('products.delete serial number')}
          onClick={() => askConfirmDeleteSerialNumber(serialNumber.value)}
          color="secondary"
        >
          <DeleteIcon />
        </IconButton>
        <IconButton
          aria-controls="serial-numbers-menu"
          aria-haspopup="true"
          disabled={disabled}
          onClick={e => setMenuAnchor(e.currentTarget)}
        >
          <MoreVert />
        </IconButton>

        <Menu
          id="serial-numbers-menu"
          anchorEl={menuAnchor}
          keepMounted
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setShowDocsModal(true);
            }}
          >
            {t('label.documents')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setShowQrModal(true);
            }}
          >
            {t('label.qr code')}
          </MenuItem>
          {/* TODO: enable later
           <MenuItem
            disabled={!serialNumber.has_history}
            onClick={() => {
              setMenuAnchor(null);
              showConfiguration({ id: product?.id || '', name: product?.name || '' }, serialNumber.value);
            }}
          >
            {t('products.download maintenance history')}
          </MenuItem> */}
        </Menu>
      </TableCell>
      <DocumentsDialog
        closeFn={() => setShowDocsModal(false)}
        docType={DocLinkType.SERIAL_NUMBER}
        open={showDocsModal}
        resourceId={serialNumber.value}
      />

      <QRDialog
        closeFn={() => setShowQrModal(false)}
        qrValue={serialNumber.qr}
        qrDescription={serialNumber.value}
        productName={product?.name || ''}
        open={showQrModal}
      />
    </TableRow>
  );
};
export { SerialNumberRow };
